import {
  setLeaseComps,
  setLoading,
  updateLeaseComp,
} from "@/features/survey/leaseCompsSlice";
import type { RootState } from "@app/store";
import { updateCurrentSurvey } from "@features/survey/surveySlice";
import type {
  CreateLeaseCompPayload,
  LeaseComp,
} from "@survey-types/survey.leaseComp.types";
import { surveyApi } from "./base";
import { surveyCoreApi } from "./survey-core";

/**
 * Lease Comps API - handles lease comp management
 * Endpoints for CRUD operations, reordering, and visibility toggling
 */
export const leaseCompsApi = surveyApi
  .enhanceEndpoints({
    addTagTypes: ["LeaseComps"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      /**
       * Fetch all lease comps for a survey
       */
      getLeaseComps: build.query<LeaseComp[], string>({
        query: (surveyId) => ({
          url: `/leaseComp/${surveyId}/all`,
          method: "GET",
        }),
        providesTags: (_result, _error, surveyId) => [
          { type: "LeaseComps", id: surveyId },
        ],
        async onQueryStarted(_surveyId, { dispatch, queryFulfilled }) {
          dispatch(setLoading(true));
          try {
            const { data } = await queryFulfilled;
            dispatch(setLeaseComps(data));
          } catch {
            dispatch(setLeaseComps([]));
          } finally {
            dispatch(setLoading(false));
          }
        },
      }),

      /**
       * Create a new lease comp
       */
      createLeaseComp: build.mutation<LeaseComp, CreateLeaseCompPayload>({
        query: (payload) => ({
          url: `/leaseComp/${payload.surveyId}`,
          method: "POST",
          body: payload,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "LeaseComps", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // Mutation errors are already handled by RTK Query
          }
        },
      }),

      /**
       * Update an existing lease comp via PATCH
       */
      updateLeaseComp: build.mutation<
        LeaseComp,
        {
          leaseCompId: string;
          updates: Partial<LeaseComp>;
          surveyId: string;
        }
      >({
        query: ({ leaseCompId, updates }) => ({
          url: `/leaseComp/${leaseCompId}`,
          method: "PATCH",
          body: updates,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "LeaseComps", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // Mutation errors are already handled by RTK Query
          }
        },
      }),

      /**
       * Delete a lease comp
       */
      deleteLeaseComp: build.mutation<
        void,
        { leaseCompId: string; surveyId: string }
      >({
        query: ({ leaseCompId }) => ({
          url: `/leaseComp/${leaseCompId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "LeaseComps", id: surveyId },
        ],
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            // Uses updateSurveyFlags to prevent Survey tag invalidation and flickering
            dispatch(
              surveyCoreApi.endpoints.updateSurveyFlags.initiate({
                id: arg.surveyId,
                marketMapBamData: '{"mapReset":true}',
              }),
            );
            dispatch(
              updateCurrentSurvey({
                id: arg.surveyId,
                marketMapBamData: '{"mapReset":true}',
              }),
            );
          } catch {
            console.error("Failed to delete lease comp");
          }
        },
      }),

      /**
       * Reorder lease comps (drag-and-drop)
       */
      reorderLeaseComps: build.mutation<
        void,
        { surveyId: string; leaseCompId: string; order: number }
      >({
        query: ({ surveyId, leaseCompId, order }) => ({
          url: `/leaseComp/reorder`,
          method: "PATCH",
          body: { surveyId, leaseCompId, order },
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "LeaseComps", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // Mutation errors are already handled by RTK Query
          }
        },
      }),

      /**
       * Update visibility of a lease comp (show/hide on map)
       */
      updateLeaseCompVisibility: build.mutation<
        LeaseComp,
        {
          leaseCompId: string;
          isHidden: boolean;
          surveyId: string;
        }
      >({
        query: ({ leaseCompId, isHidden }) => ({
          url: `/leaseComp/${leaseCompId}`,
          method: "PATCH",
          body: { isHidden },
        }),
        async onQueryStarted(
          { leaseCompId, isHidden, surveyId },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as RootState;
          const leaseCompsBackup = state.leaseComps.items;

          // Optimistically update RTK Query cache
          const cachePatch = dispatch(
            leaseCompsApi.util.updateQueryData(
              "getLeaseComps",
              surveyId,
              (draft) => {
                const lc = draft.find((l) => l.leaseCompId === leaseCompId);
                if (lc) {
                  lc.isHidden = isHidden;
                }
              },
            ),
          );

          // Optimistically update Redux slice
          dispatch(updateLeaseComp({ leaseCompId, updates: { isHidden } }));

          try {
            const { data: updatedLeaseComp } = await queryFulfilled;

            dispatch(
              surveyCoreApi.endpoints.updateSurveyFlags.initiate({
                id: updatedLeaseComp.surveyId,
                marketMapBamData: '{"mapReset":true}',
              }),
            );
          } catch (_error) {
            cachePatch.undo();
            dispatch(setLeaseComps(leaseCompsBackup));
            console.error("Error in updateLeaseCompVisibility");
          }
        },
        // No invalidatesTags — optimistic updates handle all state sync
      }),

      /**
       * Step 1: Upload lease comps Excel file — returns matched/unmatched columns
       */
      importLeaseCompsFromExcel: build.mutation<
        {
          matched: string[];
          notMatched: string[];
          unParsedColumns: string[][];
        },
        { file: File; surveyId: string }
      >({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `/import/leaseComps`,
            method: "POST",
            body: formData,
          };
        },
      }),

      /**
       * Step 2: Parse lease comps with user-confirmed column mapping
       */
      parseLeaseComps: build.mutation<
        any,
        {
          unParsedColumns: string[][];
          headerMapping: Array<{ header: string; matchedAttribute: string }>;
        }
      >({
        query: ({ unParsedColumns, headerMapping }) => ({
          url: `/import/parseLeaseComps`,
          method: "POST",
          body: { unParsedColumns, headerMapping },
        }),
      }),

      /**
       * Step 3: Bulk-import parsed lease comps into the survey
       */
      bulkImportLeaseComps: build.mutation<
        { importedLeaseComps: LeaseComp[]; count: number },
        { surveyId: string; leaseComps: Partial<LeaseComp>[] }
      >({
        query: ({ surveyId, leaseComps }) => ({
          url: `/leaseComp/bulkImport`,
          method: "POST",
          body: { surveyId, leaseComps },
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "LeaseComps", id: surveyId },
        ],
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch {
            // Mutation errors are already handled by RTK Query
          }
        },
      }),
    }),
  });
// Export hooks for each endpoint
export const {
  useGetLeaseCompsQuery,
  useCreateLeaseCompMutation,
  useUpdateLeaseCompMutation,
  useDeleteLeaseCompMutation,
  useReorderLeaseCompsMutation,
  useUpdateLeaseCompVisibilityMutation,
  useImportLeaseCompsFromExcelMutation,
  useParseLeaseCompsMutation,
  useBulkImportLeaseCompsMutation,
} = leaseCompsApi;
